"use client";
import { gtmSendEvent } from "@wojo/analytics";
import { useAuth } from "@wojo/auth";
import { useI18n } from "@wojo/localization";
import { useWishlist } from "@wojo/storyblok";
import { Button, IconButton } from "@wojo/ui";
import React from "react";
import { FiHeart } from "react-icons/fi";
import { useTourPageContext } from "../../_client/context";
import { scrollToSection } from "../../_client/hooks";
import styles from "./TourHeroSummaryButtons.module.scss";
import { TourSocialMediaShare } from "./TourSocialMediaShare";

export const TourHeroSummaryButtons: React.FC<
    React.PropsWithChildren<unknown>
> = () => {
    const { navRef, tourContent, reviewAggregate } = useTourPageContext();
    const { productPage } = useI18n("productPage");
    const { webSource } = useAuth();
    const {
        toggleWishlist,
        getIsInWishlist,
        loading: isWishlistUpdating,
    } = useWishlist();

    const tourCode = tourContent.tourCode ?? "";
    const tourName = tourContent.name ?? "";
    const cardImage = tourContent.cardImage?.filename ?? "";

    const isOnWishlist = getIsInWishlist(tourCode);
    const showWishlistButton = webSource === "INDIES";

    const wishlistLabel = isOnWishlist
        ? productPage.wishlist.removeLabel.l()
        : productPage.wishlist.addLabel.l();

    const onWishlistIconClick = async () => {
        if (isWishlistUpdating) {
            return;
        }
        !getIsInWishlist(tourCode) &&
            gtmSendEvent({
                event: "add_to_wishlist",
                ecommerce: {
                    currency: process.env.NEXT_PUBLIC_CURRENCY_CODE,
                    value: 0,
                    items: [
                        {
                            item_id: tourCode,
                            item_name: tourContent.name,
                            item_brand: process.env.NEXT_PUBLIC_BUSINESS_CODE,
                            item_category: tourContent.tour?.places
                                .filter((item) => item.type === "COUNTRY")
                                .map((item) => item.name)
                                .join(","),
                            item_category2: tourContent.tour?.places
                                .filter((item) => item.type === "CITY")
                                .map((item) => item.name)
                                .join(","),
                            item_variant: "Tour",
                            market: process.env.NEXT_PUBLIC_MARKET_CODE,
                            tourCode: tourCode,
                            price: 0,
                            quantity: 1,
                            activityLevel:
                                tourContent.tour?.activityLevelContent?.name,
                            classLevel:
                                tourContent.tour?.classLevelContent?.name,
                            rating: reviewAggregate?.avgRating,
                        },
                    ],
                },
                ga4Tracking: {
                    ga4_object: "TOUR",
                    ga4_action: "ADD_TO_WISHLIST",
                    tourCode: tourCode,
                    tourName: tourContent.name ?? "",
                    parentObject: "TOUR_PAGE",
                },
            });
        await toggleWishlist(tourCode);
    };

    const continent =
        tourContent.tour?.places.find((item) => item.type === "CONTINENT")
            ?.name ?? "Continent";
    const tripBuilderButtonLabel =
        productPage.tourHero.tripBuilderButtonLabel.l();

    return (
        <div className={styles["tour-hero-summary-buttons-wrapper"]}>
            <Button
                variant="primary"
                palette="monochrome"
                tag="a"
                href="#tripbuilder"
                onClick={(e) => {
                    gtmSendEvent({
                        event: "track",
                        eventCategory: "product page",
                        eventAction: "header clicks",
                        eventLabel: "trip-builder",
                        ga4_action: "ELEMENT",
                        ga4_object: "CLICKED",
                        elementType: "button",
                        clickText: tripBuilderButtonLabel,
                    });
                    scrollToSection(
                        e as React.MouseEvent<HTMLAnchorElement>,
                        navRef,
                    );
                }}
            >
                {tripBuilderButtonLabel}
            </Button>
            {showWishlistButton && (
                <IconButton
                    variant="secondary"
                    palette="monochrome"
                    title={wishlistLabel}
                    icon={
                        <FiHeart
                            fill={isOnWishlist ? "currentColor" : "transparent"}
                            aria-hidden
                        />
                    }
                    onClick={() => {
                        onWishlistIconClick();
                    }}
                    ariaLabel={wishlistLabel}
                    style={{ padding: 0 }}
                />
            )}
            <TourSocialMediaShare
                tourName={tourName}
                tourCode={tourCode}
                continent={continent}
                cardImage={cardImage}
            />
        </div>
    );
};
